import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuKeyRound, LuLaptop, LuLoader2, LuPencil, LuSmartphone } from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import { getUser, storeUser } from "../../api-services/Process";
import AdminLayout from "../../app-components/AdminLayout";
import ConfirmDialog from "../../app-components/ui/ConfirmDialog";
import PageHeader from "../../app-components/ui/PageHeader";
import { btnPrimary, btnSecondary, card, inputBase } from "../../app-components/ui/styles";
import { SITELINKS } from "../../app-routes/links";
import { getErrorMessage } from "../../utils";
import notify from "../../utils/notify";
import { timeAgo } from "../../app-components/header/notificationStyle";

type Session = {
  id: number;
  device: string;
  ip: string | null;
  signed_in_at: string | null;
  last_active_at: string | null;
  expires_at: string | null;
  current: boolean;
};

type Account = { admin: { id: number; name: string; email: string; created_at: string | null }; sessions: Session[] };

const isPhone = (device: string) => /iOS|Android/.test(device);

const MyAccount = () => {
  const [data, setData] = useState<Account | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res: any = await apiClient.get("account");
      setData(res?.data ?? null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) return notify.error("Enter your name (at least 2 characters).");
    setSavingName(true);
    try {
      await apiClient.patch("account", { name: name.trim() });
      storeUser({ ...(getUser() ?? {}), name: name.trim() }); // header greeting uses the stored user
      notify.success("Name updated.");
      setEditing(false);
      load();
    } catch (err) {
      notify.error(`Couldn't update your name: ${getErrorMessage(err)}`);
    } finally {
      setSavingName(false);
    }
  };

  const signOutSession = async (s: Session) => {
    setBusyId(s.id);
    try {
      await apiClient.delete(`account/sessions/${s.id}`);
      notify.success(`Signed out of ${s.device}.`);
      load();
    } catch (err) {
      notify.error(`Couldn't sign that session out: ${getErrorMessage(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const signOutOthers = async () => {
    setSigningOutAll(true);
    try {
      const res: any = await apiClient.post("account/sessions/revoke-others");
      notify.success(res?.data?.message ?? "Signed out everywhere else.");
      setConfirmAll(false);
      load();
    } catch (err) {
      notify.error(`Couldn't sign out other sessions: ${getErrorMessage(err)}`);
    } finally {
      setSigningOutAll(false);
    }
  };

  const others = data?.sessions.filter((s) => !s.current).length ?? 0;
  const when = (iso: string | null) => (iso ? timeAgo(iso) : "–");

  return (
    <AdminLayout title="Admin | My account">
      <PageHeader title="My account" subtitle="Your admin profile and where you're signed in." />

      {error && !data ? (
        <div className={`${card} flex flex-col items-center px-6 py-14 text-center`}>
          <p className="mb-1 font-semibold text-honeConnectBlack">Couldn't load your account</p>
          <p className="mb-4 max-w-md text-sm text-hcMuted">{error}</p>
          <button onClick={load} className={btnPrimary}>Try again</button>
        </div>
      ) : !data ? (
        <div className={`${card} flex justify-center py-14`}>
          <LuLoader2 className="h-6 w-6 animate-spin text-hcMuted" aria-hidden />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          {/* profile */}
          <section className={`${card} p-5`}>
            <h2 className="mb-4 text-[15px] font-semibold text-honeConnectBlack">Profile</h2>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-xs font-medium text-hcMuted">Name</dt>
                {editing ? (
                  <form onSubmit={saveName} className="mt-1 flex flex-wrap gap-2">
                    <input autoFocus value={name} onChange={(e) => setName(e.target.value)} aria-label="Your name" className={`${inputBase} min-w-0 flex-1 py-2`} />
                    <button type="submit" disabled={savingName} className={`${btnPrimary} py-2`}>{savingName ? "Saving" : "Save"}</button>
                    <button type="button" onClick={() => setEditing(false)} className={`${btnSecondary} py-2`}>Cancel</button>
                  </form>
                ) : (
                  <dd className="mt-0.5 flex items-center gap-2 font-medium text-honeConnectBlack">
                    {data.admin.name}
                    <button
                      type="button"
                      onClick={() => { setName(data.admin.name); setEditing(true); }}
                      aria-label="Edit name"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-honeConnectGray hover:bg-hcBg hover:text-honeConnectBlack"
                    >
                      <LuPencil className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </dd>
                )}
              </div>
              <div>
                <dt className="text-xs font-medium text-hcMuted">Email</dt>
                <dd className="mt-0.5 font-medium text-honeConnectBlack">{data.admin.email}</dd>
                <dd className="mt-1 text-xs text-hcMuted">Your 2FA codes go here. To change it, ask another admin.</dd>
              </div>
              {data.admin.created_at && (
                <div>
                  <dt className="text-xs font-medium text-hcMuted">Admin since</dt>
                  <dd className="mt-0.5 text-honeConnectBlack">
                    {new Date(data.admin.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
                  </dd>
                </div>
              )}
            </dl>
            <Link to={SITELINKS.CHANGE_PASSWORD} className={`${btnSecondary} mt-6 inline-flex items-center gap-2`}>
              <LuKeyRound className="h-4 w-4" aria-hidden />
              Change password
            </Link>
          </section>

          {/* sessions */}
          <section className={`${card} p-5 xl:col-span-2`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-[15px] font-semibold text-honeConnectBlack">Where you're signed in</h2>
                <p className="mt-0.5 text-xs text-hcMuted">Each sign-in lasts 12 hours. Sign out any you don't recognise, then change your password.</p>
              </div>
              <button type="button" onClick={() => setConfirmAll(true)} disabled={!others} className={`${btnSecondary} py-2 text-sm`}>
                Sign out everywhere else
              </button>
            </div>

            <ul className="divide-y divide-hcBorder">
              {data.sessions.map((s) => {
                const Icon = isPhone(s.device) ? LuSmartphone : LuLaptop;
                return (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 py-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hcBg text-honeConnectGray">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-honeConnectBlack">
                        {s.device}
                        {s.current && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">This device</span>
                        )}
                      </p>
                      <p className="text-xs text-hcMuted">
                        {[s.ip, `signed in ${when(s.signed_in_at)}`, s.last_active_at ? `active ${when(s.last_active_at)}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    {!s.current && (
                      <button
                        type="button"
                        onClick={() => signOutSession(s)}
                        disabled={busyId === s.id}
                        className="text-sm font-medium text-red-600 hover:underline disabled:opacity-60"
                      >
                        {busyId === s.id ? "Signing out" : "Sign out"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      )}

      <ConfirmDialog
        open={confirmAll}
        tone="primary"
        title={`Sign out of ${others} other session${others === 1 ? "" : "s"}?`}
        message="You'll stay signed in here. Any other device will need your password and a 2FA code to get back in."
        confirmLabel="Sign out everywhere else"
        loading={signingOutAll}
        onConfirm={signOutOthers}
        onCancel={() => setConfirmAll(false)}
      />
    </AdminLayout>
  );
};

export default MyAccount;
